import React, { useState } from 'react';
import { supabase } from '../config/supabase';

const CTASection: React.FC = () => {
  const [phone, setPhone] = useState('');
  const [bestTime, setBestTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleCallbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!phone.trim()) {
      setShowError(true);
      return;
    }

    setIsSubmitting(true);
    setShowError(false);

    try {
      // Clean phone number
      const cleanPhone = phone.replace(/\D/g, '');

      const insertData = {
        phone: cleanPhone,
        best_time: bestTime || 'anytime',
        source: 'cta_section'
      };

      const { error } = await supabase
        .from('quick_callbacks')
        .insert([insertData]);

      if (error) {
        console.error('Supabase error:', error);
        setShowError(true);
      } else {
        setShowSuccess(true);
        setPhone('');
        setBestTime('');

        // Google Analytics tracking
        if (typeof (window as any).gtag !== 'undefined') {
          (window as any).gtag('event', 'contact_info_submitted', {
            event_category: 'lead_generation',
            event_label: 'cta_callback',
            custom_parameters: {
              source: 'cta_section',
              best_time: bestTime || 'anytime'
            }
          });

          // Google Ads conversion tracking
          (window as any).gtag('event', 'conversion', {
            'send_to': 'AW-17868365802/pBSECPKF4OobEOq_pshC',
            'value': 500.0,
            'currency': 'CAD'
          });
        }

        // Hide success message after 5 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Error submitting callback request:', error);
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-accent py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-4xl font-bold text-white mb-6">Ready to Transform Your Space?</h2>
        <p className="text-xl text-green-100 mb-8">
          Get your instant quote in under 1 minute or book a free consultation.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <a
            href="/pergola-quote"
            className="bg-white text-accent px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
          >
            ⚡ Get Instant Pergola Quote
          </a>
          <a
            href="/awning-quote"
            className="bg-white text-accent px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
          >
            ⚡ Get Instant Awning Quote
          </a>
          <a
            href="https://calendly.com/evieoutdoors/free-outdoor-living-consultation"
            target="_blank"
            rel="noopener noreferrer"
            className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-accent transition-colors inline-block"
          >
            Schedule Design Visit
          </a>
        </div>

        {/* Quick Callback Form */}
        <div className="bg-white/95 backdrop-blur-sm rounded-xl p-8 max-w-md mx-auto shadow-2xl">
          <h3 className="text-2xl font-bold text-primary mb-4">Or Get a Quick Callback</h3>
          <p className="text-gray-600 mb-6">We'll call you within 24 hours</p>

          <form onSubmit={handleCallbackSubmit} className="space-y-4">
            <div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Your phone number"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                required
              />
            </div>

            <div>
              <select
                value={bestTime}
                onChange={(e) => setBestTime(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
              >
                <option value="">Best time to call (optional)</option>
                <option value="morning">Morning (9 AM - 12 PM)</option>
                <option value="afternoon">Afternoon (12 PM - 5 PM)</option>
                <option value="evening">Evening (5 PM - 8 PM)</option>
                <option value="anytime">Anytime</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-accent text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : '📞 Request Callback'}
            </button>
          </form>

          {/* Success Message */}
          {showSuccess && (
            <div className="mt-3 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              <p>✅ Thank you! We'll call you within 24 hours.</p>
            </div>
          )}

          {/* Error Message */}
          {showError && (
            <div className="mt-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              <p>❌ Something went wrong. Please try again.</p>
            </div>
          )}
        </div>

        <p className="text-green-100 mt-8 text-sm">
          No obligation • Free consultation • Professional installation included
        </p>
      </div>
    </section>
  );
};

export default CTASection;
